import React from "react";
import { Link } from "react-router-dom";
import { Plane, Globe2, Crown, Sparkles, ArrowRight, CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";

const TRAVEL_IMG = "https://images.pexels.com/photos/35026952/pexels-photo-35026952.jpeg";

const destinations = [
  { city: "Dubai", country: "UAE", img: "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=900&q=80" },
  { city: "Tokyo", country: "Japan", img: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?auto=format&fit=crop&w=900&q=80" },
  { city: "Maldives", country: "Indian Ocean", img: "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&w=900&q=80" },
  { city: "Paris", country: "France", img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=900&q=80" },
  { city: "Bali", country: "Indonesia", img: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=900&q=80" },
  { city: "New York", country: "USA", img: "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?auto=format&fit=crop&w=900&q=80" },
];

export default function TravelPage() {
  const [date, setDate] = React.useState();

  return (
    <div data-testid="travel-page">
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <img src={TRAVEL_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#030712]/80 via-[#030712]/80 to-[#030712]" />
        <div className="mfb-container relative">
          <div className="overline mb-4">Travel Services</div>
          <h1 className="font-[Outfit] text-5xl sm:text-6xl font-light tracking-tighter text-white max-w-4xl leading-[1.05]">
            Travel like you <span className="gradient-text-gold">manage capital</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-slate-300 text-base leading-relaxed">
            Premium metal multi-currency travel cards. Airport lounges. Concierge bookings to 800+ destinations.
            Designed for those who travel often, and travel well.
          </p>

          <div className="mt-10 glass rounded-2xl p-5 max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-sky-400 mb-3">Plan Your Trip</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <input
                placeholder="From"
                data-testid="travel-from"
                className="bg-[#111827] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500"
              />
              <input
                placeholder="To"
                data-testid="travel-to"
                className="bg-[#111827] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500"
              />
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    data-testid="travel-date-trigger"
                    className="flex items-center gap-2 bg-[#111827] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-left text-white hover:border-sky-500/40"
                  >
                    <CalendarDays className="w-4 h-4 text-sky-400" />
                    {date ? format(date, "PPP") : <span className="text-slate-500">Pick a date</span>}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#0B1221] border-white/10 text-white" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="mt-4 flex justify-end">
              <Link to="/contact" data-testid="travel-cta-enquire" className="btn-gold">Enquire Now <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mfb-section">
        <div className="mfb-container">
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { i: Crown, t: "Metal Travel Cards", d: "Multi-currency Visa Infinite & Mastercard World Elite cards." },
              { i: Globe2, t: "Lounge Access", d: "1,300+ airport lounges via Priority Pass + DragonPass." },
              { i: Sparkles, t: "Concierge", d: "24/7 concierge for flights, hotels and luxury experiences." },
            ].map((x, i) => (
              <div key={i} className="card-luxury p-7" data-testid={`travel-feature-${i}`}>
                <x.i className="w-6 h-6 text-sky-400" />
                <div className="mt-4 text-white font-[Outfit] text-xl">{x.t}</div>
                <div className="mt-2 text-slate-400 text-sm leading-relaxed">{x.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mfb-section border-t border-white/5">
        <div className="mfb-container">
          <div className="overline mb-3">Curated Destinations</div>
          <h2 className="font-[Outfit] text-4xl font-light text-white max-w-2xl">800+ destinations. Hand-picked stays. Member rates.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {destinations.map((d) => (
              <div key={d.city} className="card-luxury aspect-[4/5] relative overflow-hidden group" data-testid={`travel-dest-${d.city.toLowerCase()}`}>
                <img src={d.img} alt={d.city} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-[#030712]/30 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <Plane className="w-5 h-5 text-[#D4AF37]" />
                  <div className="font-[Outfit] text-2xl text-white mt-2">{d.city}</div>
                  <div className="text-slate-300 text-xs uppercase tracking-widest mt-0.5">{d.country}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
